import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Container,
  Grid,
  Theme,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import { Add } from '@material-ui/icons';

import Header from '../components/Header';
import Section from '../components/Section';
import DiscussionItem from '../components/DiscussionItem';
import ChatBox from '../components/ChatBox';
import PreFooter from '../components/PreFooter';
import Footer from '../components/Footer';

import ForumCreateDialog, { IHandler } from '../components/ForumCreateDialog';
import {
  convertTopicDiscussion,
  createTopic,
  fetchTopics,
} from '../services/forum';
import { auth } from '../services/firebase';
import { useHistory } from 'react-router-dom';
import { cleanText } from '../services/rude-word';
interface IPageState {
  prevAble: boolean;
  nextAble: boolean;
}

const useStyles = makeStyles<Theme>((theme: Theme) => ({
  createButton: {
    backgroundColor: '#7683F9',
    borderRadius: 16,
  },
  formControl: {
    marginRight: theme.spacing(2),
  },
  input: {
    backgroundColor: '#FFF',
    color: '#666',
  },
  pagingButton: {
    marginRight: theme.spacing(1),
    minWidth: 32,
  },
}));
type Document = firebase.default.firestore.QueryDocumentSnapshot<
  firebase.default.firestore.DocumentData
>;
const limit = 5;
const Forum = () => {
  const [forumCreatorState, setForumCreatorState] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState(false);
  const creatorRef = useRef<IHandler | null>(null);
  const { createButton, pagingButton } = useStyles();
  const visibleRef = useRef<Document[]>([]);
  const [fetching, setFetching] = useState<boolean>(false);
  const [pageState, setPageState] = useState<IPageState>({
    nextAble: false,
    prevAble: false,
  });
  const pageRef = useRef<number>(0);
  const [discussions, setDiscussions] = useState<ITopicDiscussion[]>([]);
  const history = useHistory();

  const fetch = useCallback(async () => {
    setFetching(true);
    const result = await fetchTopics({ limit });
    if (result) {
      const [docs, hasOtherPage] = result;
      setPageState({ prevAble: false, nextAble: hasOtherPage });
      if (docs.length > 0) {
        visibleRef.current = [docs[0].ref, docs[docs.length - 1].ref];
      }
      pageRef.current = 0;
      setDiscussions(docs.map((doc) => convertTopicDiscussion(doc)));
    }
    setFetching(false);
  }, []);
  useEffect(() => {
    fetch();
  }, [fetch]);
  const handleClickBack = async () => {
    if (fetching) return;
    setFetching(true);
    pageRef.current--;
    const firstPage = pageRef.current === 0;
    if (firstPage) {
      return await fetch();
    } else {
      const result = await fetchTopics({
        limit,
        action: 'previous',
        lastVisible: visibleRef.current[0],
      });
      if (result) {
        const [docs, hasOtherPage] = result;
        setPageState({ nextAble: true, prevAble: hasOtherPage });
        visibleRef.current = [docs[0].ref, docs[docs.length - 1].ref];
        setDiscussions(docs.map((doc) => convertTopicDiscussion(doc)));
      }
    }
    setFetching(false);
  };
  const handleClickNext = async () => {
    if (fetching) return;
    setFetching(true);
    pageRef.current++;
    const result = await fetchTopics({
      limit,
      action: 'next',
      lastVisible: visibleRef.current[1],
    });
    if (result) {
      const [docs, hasOtherPage] = result;
      setPageState({ prevAble: true, nextAble: hasOtherPage });
      visibleRef.current = [docs[0].ref, docs[docs.length - 1].ref];
      setDiscussions(docs.map((doc) => convertTopicDiscussion(doc)));
    }
    setFetching(false);
  };
  const handleSubmitForumTopic = async (discussion: ITopicDiscussion) => {
    if (auth.currentUser) {
      setSubmitting(true);
      const [cleanTitle, cleanContent] = await Promise.all([
        cleanText(discussion.title),
        cleanText(discussion.content),
      ]);
      const [isCleanTitle, isCleanContent] = [true, true];
      //old cleantext
      //await Promise.all([
      //   cleanText(discussion.title),
      //   cleanText(discussion.content),
      // ]);
      if (isCleanTitle && isCleanContent) {
        await createTopic({
          ...discussion,
          title: cleanTitle,
          content: cleanContent,
          author: auth.currentUser.displayName ?? '',
          avatar: auth.currentUser.photoURL ?? '',
          uid: auth.currentUser.uid,
        });
        creatorRef.current?.clear();
        await fetch();
        setForumCreatorState(false);
      } else {
        creatorRef.current?.badWord();
      }
      setSubmitting(false);
    }
  };
  return (
    <>
      <Header />
      <Container maxWidth="md">
        <Section>
          <Typography variant="h4">ฟอรั่ม</Typography>
          <Box height={16} />
          <Grid container spacing={1}>
            <Grid item xs={12} md={8}>
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                p={4}
                bgcolor="#564F70"
                mb={1}
              >
                <Typography variant="h5">
                  พื้นที่สำหรับพูดคุย แลกเปลี่ยนความคิดเห็น
                </Typography>
                <Box height={16} />
                <Typography align="center">
                  ร่วมพูดคุย แสดงความคิดเห็น
                  และแบ่งปันประสบการณ์เกี่ยวกับความมั่นคงปลอดภัยทางไซเบอร์
                  เพื่อป้องกันอันตรายที่อาจเกิดขึ้นจากความรู้ไม่เท่าทันเจตนาของผู้ประสงค์ร้ายที่คอยฉกฉวยผลประโยชน์จากผู้อื่นโดยไม่ได้รับอนุญาต
                </Typography>
                <Box height={24} />
                <Button
                  className={createButton}
                  variant="contained"
                  color="primary"
                  onClick={() => setForumCreatorState(true)}
                  disabled={auth.currentUser === null}
                >
                  <Add fontSize="small" />
                  สร้างกระทู้
                </Button>
              </Box>
              {discussions.map((discussion, index) => (
                <DiscussionItem
                  key={index}
                  discussion={discussion}
                  style={{ marginBottom: 2, cursor: 'pointer' }}
                  onClick={() => {
                    history.push(`/discussion/${discussion.docId}`);
                  }}
                />
              ))}

              <Box
                display="flex"
                bgcolor="#454057"
                p={2}
                justifyContent="center"
                alignItems="center"
              >
                <Button
                  className={pagingButton}
                  variant="outlined"
                  color="secondary"
                  onClick={handleClickBack}
                  disabled={fetching || !pageState.prevAble}
                >
                  &lt;
                </Button>
                <Button
                  className={pagingButton}
                  variant="outlined"
                  color="secondary"
                  onClick={handleClickNext}
                  disabled={fetching || !pageState.nextAble}
                >
                  &gt;
                </Button>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <ChatBox />
            </Grid>
          </Grid>
        </Section>
      </Container>
      <PreFooter />
      <Footer />
      <ForumCreateDialog
        onSubmit={handleSubmitForumTopic}
        open={forumCreatorState}
        onClose={() => {
          setForumCreatorState(false);
        }}
        submitting={submitting}
        ref={creatorRef}
      />
    </>
  );
};

export default Forum;
